"use client";

import { type ReactNode, useEffect, useState } from "react";

/* ── OrgTopBar ─────────────────────────────────────────────────────────── */

export interface OrgTopBarProps {
  /**
   * Breadcrumb / section label rendered on the left side of the sticky top
   * bar. When undefined the top bar renders nothing (useful for overview
   * pages that don't need it).
   */
  children?: ReactNode;
}

/**
 * Sticky top bar for org section-detail pages. Keeps breadcrumbs in view
 * while the user scrolls. Renders nothing when `children` is not provided.
 */
export function OrgTopBar({ children }: OrgTopBarProps) {
  if (!children) return null;
  return (
    <div
      className={[
        "sticky top-0 z-10 py-2",
        "h-14 bg-(--bg-white)",
        "border-b border-(--border-subtle)",
        "box-border",
      ].join(" ")}
    >
      <div className="h-full px-6">
        <div className="h-full flex items-center justify-between gap-4">
          <div className="flex items-center flex-1 min-w-0 h-full">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── OrgSaveBar ────────────────────────────────────────────────────────── */

export type OrgSaveBarValidity = {
  isValid: boolean;
  payerValid: boolean;
  payerTotal: number;
  specialtyValid: boolean;
  specialtyTotal: number;
};

export interface OrgSaveBarProps {
  /** Whether there are unsaved changes. */
  isDirty: boolean;
  /** Pending change items shown in the changes modal. */
  changes: { id: string; label: string }[];
  /** Unix timestamp (ms) of the last successful save. */
  lastSavedAt?: number;
  /** Current validation state; blocks save when invalid. */
  validity: OrgSaveBarValidity;
  /** Called when the user confirms the save action. */
  onSave: () => void;
  /** Called when the user reverts all changes. */
  onRevert: () => void;
  /**
   * Optional render prop for the changes modal. Receives
   * `{ onClose, onRevert }`.
   */
  renderChangesModal?: (props: {
    onClose: () => void;
    onRevert: () => void;
  }) => ReactNode;
}

/**
 * Fixed bottom bar — always visible on every organization page. Houses the
 * pending-changes pill and the save button. Designed to align flush with the
 * side-nav profile row at the bottom of the viewport.
 */
export function OrgSaveBar({
  isDirty,
  changes,
  lastSavedAt,
  validity,
  onSave,
  onRevert,
  renderChangesModal,
}: OrgSaveBarProps) {
  const [changesOpen, setChangesOpen] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    if (!justSaved) return;
    const t = setTimeout(() => setJustSaved(false), 1500);
    return () => clearTimeout(t);
  }, [justSaved]);

  function handleSave() {
    if (!validity.isValid) return;
    onSave();
    setJustSaved(true);
  }

  const blocked = isDirty && !validity.isValid;
  const saveLabel = justSaved
    ? "Saved"
    : blocked
      ? "Resolve errors to save"
      : isDirty
        ? "Save changes"
        : "Saved";

  let title: string | undefined = lastSavedAt
    ? `Last saved ${formatAgo(lastSavedAt)}`
    : undefined;

  if (blocked) {
    const issues: string[] = [];
    if (!validity.payerValid)
      issues.push(`Payer Mix totals ${validity.payerTotal.toFixed(1)}%`);
    if (!validity.specialtyValid)
      issues.push(
        `Specialty Mix totals ${validity.specialtyTotal.toFixed(1)}%`,
      );
    if (issues.length) title = `${issues.join(" · ")} (must equal 100%)`;
  }

  return (
    <div
      className={[
        "fixed bottom-0 left-60 right-0 z-40",
        "h-[47px]",
        "bg-(--bg-canvas) border-t border-(--border-subtle)",
        "box-border",
      ].join(" ")}
    >
      <div className="h-full px-6">
        <div className="h-full flex items-center justify-between gap-4">
          {/* Left spacer */}
          <div className="flex items-center flex-1 min-w-0 h-full" />

          {/* Right: changes pill + save */}
          <div className="inline-flex items-center gap-3 shrink-0 h-full">
            {/* Changes pill */}
            {isDirty && changes.length > 0 && (
              <button
                type="button"
                className={[
                  "inline-flex items-center gap-1.5",
                  "h-8 px-3",
                  "rounded-[3px]",
                  "bg-(--bg-white) border border-(--border-subtle)",
                  "text-(--fg-secondary)",
                  "cursor-pointer",
                  "transition-[background,color] duration-120",
                  "hover:bg-(--bg-focused) hover:text-(--fg-primary)",
                ].join(" ")}
                onClick={() => setChangesOpen(true)}
                aria-label={`${changes.length} changes since last save. Open details.`}
              >
                <span className="font-mono text-sm font-semibold tabular-nums tracking-[-0.02em] text-(--fg-primary)">
                  {changes.length}
                </span>
                <span className="text-xs">
                  change{changes.length === 1 ? "" : "s"} since last save
                </span>
              </button>
            )}

            {/* Save button */}
            <button
              type="button"
              className={[
                "inline-flex items-center gap-2",
                "h-9 px-4.5",
                "rounded-(--radius-field)",
                "text-sm font-medium",
                "transition-[background,color,opacity] duration-120",
                blocked
                  ? "bg-(--status-error-bg) text-(--status-error-text) cursor-not-allowed"
                  : isDirty || justSaved
                    ? "bg-(--brand-navy) text-(--fg-inverse) cursor-pointer hover:bg-(--steel-110)"
                    : "bg-(--bg-canvas) text-(--fg-muted) cursor-default",
              ].join(" ")}
              onClick={handleSave}
              disabled={!isDirty || blocked}
              title={title}
            >
              {justSaved && (
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden={true}
                >
                  <path d="M5 12.5l4 4 10-10" />
                </svg>
              )}
              {saveLabel}
            </button>
          </div>
        </div>
      </div>

      {/* Changes modal */}
      {changesOpen &&
        renderChangesModal?.({
          onClose: () => setChangesOpen(false),
          onRevert: () => {
            onRevert();
            setChangesOpen(false);
          },
        })}
    </div>
  );
}

function formatAgo(ts: number): string {
  const s = Math.max(1, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}
